import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Phone, 
  ChevronDown, 
  Flame, 
  Wind, 
  Refrigerator,
  MapPin,
  MessageCircle,
  Wrench,
  AlertTriangle,
  FileText,
  Info,
  Mail
} from 'lucide-react';
import { trackButtonClick, trackPhoneClick, trackWhatsAppClick } from '../utils/analytics';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const userRole = localStorage.getItem('userRole');

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.nav-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isActiveGroup = (paths) => paths.some(p => location.pathname.startsWith(p));

  const handlePhoneClick = () => {
    trackPhoneClick('05518478521', 'mobile_bar');
  };

  const handleWhatsAppClick = () => {
    trackWhatsAppClick('05518478521', 'mobile_bar');
  };

  const toggleDropdown = (name, e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const closeMenuAndDropdown = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  // Service menu items
  const serviceMenuItems = [
    { 
      name: 'Kombi Servisi', 
      path: '/servisler#kombi', 
      icon: <Flame size={18} />,
      description: 'Bakım, onarım, montaj'
    },
    { 
      name: 'Klima Servisi', 
      path: '/servisler#klima', 
      icon: <Wind size={18} />,
      description: 'Montaj, gaz dolumu, temizlik'
    },
    { 
      name: 'Beyaz Eşya Servisi', 
      path: '/servisler#beyaz-esya', 
      icon: <Refrigerator size={18} />,
      description: 'Buzdolabı, çamaşır, bulaşık'
    },
    { 
      name: 'Tüm Servisler', 
      path: '/servisler', 
      icon: <Wrench size={18} />,
      description: 'Tüm hizmetlerimiz'
    }
  ];

  // Location menu items
  const locationMenuItems = [
    { 
      name: 'Kuşadası', 
      path: '/kusadasi-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    },
    { 
      name: 'Söke', 
      path: '/soke-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    },
    { 
      name: 'Aydın', 
      path: '/aydin-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    },
    { 
      name: 'Güzelçamlı', 
      path: '/guzelcamli-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    },
    { 
      name: 'Davutlar', 
      path: '/davutlar-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    },
    { 
      name: 'İncirliova', 
      path: '/incirliova-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    },
    { 
      name: 'Germencik', 
      path: '/germencik-kombi-servisi',
      services: ['Kombi', 'Klima', 'Beyaz Eşya']
    }
  ];

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      {/* Main Header */}
      <div className="header-main">
        <div className="container">
          <div className="header-content">
            <Link to="/" className="logo" onClick={closeMenuAndDropdown}>
              <img 
                src="https://customer-assets.emergentagent.com/job_ev-aletleri-servis/artifacts/vs6pzaqs_1.png" 
                alt="Kuşadası Söke Aydın Kombi Servisi Beyaz Eşya Tamiri Klima Montaj - teknikservisburda.com" 
                className="logo-image"
              />
              <div className="logo-text">
                <span className="logo-name">teknikservisburda.com</span>
                <span className="logo-tagline">Profesyonel Teknik Servis</span>
              </div>
            </Link>

            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <Link 
                to="/" 
                className={`nav-link ${isActive('/') ? 'active' : ''}`} 
                onClick={closeMenuAndDropdown}
              >
                Ana Sayfa
              </Link>

              {/* Servislerimiz Dropdown */}
              <div className={`nav-dropdown ${activeDropdown === 'services' ? 'open' : ''}`}>
                <button 
                  className={`nav-link dropdown-trigger ${isActiveGroup(['/servisler']) ? 'active' : ''}`}
                  onClick={(e) => toggleDropdown('services', e)}
                >
                  Servislerimiz
                  <ChevronDown size={16} className="dropdown-arrow" />
                </button>
                <div className="dropdown-menu services-dropdown">
                  <div className="dropdown-grid">
                    {serviceMenuItems.map((item) => (
                      <Link 
                        key={item.path} 
                        to={item.path} 
                        className="dropdown-item"
                        onClick={closeMenuAndDropdown}
                      >
                        <span className="dropdown-item-icon">{item.icon}</span>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">{item.name}</span>
                          <span className="dropdown-item-desc">{item.description}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bölgeler Dropdown */}
              <div className={`nav-dropdown ${activeDropdown === 'locations' ? 'open' : ''}`}>
                <button 
                  className={`nav-link dropdown-trigger ${isActiveGroup(['/kusadasi', '/soke', '/aydin', '/didim']) ? 'active' : ''}`}
                  onClick={(e) => toggleDropdown('locations', e)}
                >
                  <MapPin size={16} />
                  Bölgeler
                  <ChevronDown size={16} className="dropdown-arrow" />
                </button>
                <div className="dropdown-menu locations-dropdown">
                  <div className="dropdown-locations">
                    {locationMenuItems.map((item) => (
                      <Link 
                        key={item.path} 
                        to={item.path} 
                        className="dropdown-location-item"
                        onClick={closeMenuAndDropdown}
                      >
                        <MapPin size={16} />
                        <div>
                          <span className="location-name">{item.name}</span>
                          <span className="location-services">{item.services.join(' • ')}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Arıza Kodları Dropdown */}
              <div className={`nav-dropdown ${activeDropdown === 'errors' ? 'open' : ''}`}>
                <button 
                  className={`nav-link dropdown-trigger ${isActiveGroup(['/kombi-ariza-kodlari', '/klima-ariza-kodlari']) ? 'active' : ''}`}
                  onClick={(e) => toggleDropdown('errors', e)}
                >
                  <AlertTriangle size={16} />
                  Arıza Kodları
                  <ChevronDown size={16} className="dropdown-arrow" />
                </button>
                <div className="dropdown-menu">
                  <div className="dropdown-grid">
                    <Link 
                      to="/kombi-ariza-kodlari" 
                      className="dropdown-item"
                      onClick={closeMenuAndDropdown}
                    >
                      <span className="dropdown-item-icon"><Flame size={18} /></span>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Kombi Arıza Kodları</span>
                        <span className="dropdown-item-desc">Demirdöküm, Vaillant, Bosch, Buderus</span>
                      </div>
                    </Link>
                    <Link 
                      to="/klima-ariza-kodlari" 
                      className="dropdown-item"
                      onClick={closeMenuAndDropdown}
                    >
                      <span className="dropdown-item-icon"><Wind size={18} /></span>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Klima Arıza Kodları</span>
                        <span className="dropdown-item-desc">Arçelik, Vestel, Toshiba, Gree, Bosch</span>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>

              <Link 
                to="/blog" 
                className={`nav-link ${isActive('/blog') || location.pathname.startsWith('/blog/') ? 'active' : ''}`} 
                onClick={closeMenuAndDropdown}
              >
                <FileText size={16} />
                Blog
              </Link>

              <Link 
                to="/hakkimizda" 
                className={`nav-link ${isActive('/hakkimizda') ? 'active' : ''}`} 
                onClick={closeMenuAndDropdown}
              >
                <Info size={16} />
                Hakkımızda
              </Link>

              <Link 
                to="/iletisim" 
                className={`nav-link ${isActive('/iletisim') ? 'active' : ''}`} 
                onClick={closeMenuAndDropdown}
              >
                <Mail size={16} />
                İletişim
              </Link>

              {/* Giriş Dropdown - Mobile Only */}
              {!userRole && (
                <div className={`nav-dropdown mobile-only-login ${activeDropdown === 'login-mobile' ? 'open' : ''}`}>
                  <button 
                    className={`nav-link dropdown-trigger`}
                    onClick={(e) => toggleDropdown('login-mobile', e)}
                  >
                    Giriş
                    <ChevronDown size={16} className="dropdown-arrow" />
                  </button>
                  <div className="dropdown-menu">
                    <div className="dropdown-grid">
                      <Link 
                        to="/giris" 
                        state={{ role: 'admin' }}
                        className="dropdown-item"
                        onClick={closeMenuAndDropdown}
                      >
                        <span className="dropdown-item-icon"><Wrench size={18} /></span>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Yönetici Girişi</span>
                          <span className="dropdown-item-desc">Admin paneli</span>
                        </div>
                      </Link>
                      <Link 
                        to="/giris" 
                        state={{ role: 'technician' }}
                        className="dropdown-item"
                        onClick={closeMenuAndDropdown}
                      >
                        <span className="dropdown-item-icon"><Wrench size={18} /></span>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Teknisyen Girişi</span>
                          <span className="dropdown-item-desc">Teknisyen paneli</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {userRole === 'admin' && (
                <Link 
                  to="/admin" 
                  className={`nav-link ${isActive('/admin') ? 'active' : ''}`} 
                  onClick={closeMenuAndDropdown}
                >
                  Yönetim Paneli
                </Link>
              )}
              {userRole === 'technician' && (
                <Link 
                  to="/teknisyen" 
                  className={`nav-link ${isActive('/teknisyen') ? 'active' : ''}`} 
                  onClick={closeMenuAndDropdown}
                >
                  Teknisyen Paneli
                </Link>
              )}
            </nav>

            <div className="header-actions">
              {/* Giriş Dropdown */}
              <div className={`nav-dropdown login-dropdown ${activeDropdown === 'login' ? 'open' : ''}`}>
                <button 
                  className="btn-secondary login-btn"
                  onClick={(e) => toggleDropdown('login', e)}
                >
                  Giriş
                  <ChevronDown size={16} className="dropdown-arrow" />
                </button>
                <div className="dropdown-menu login-menu">
                  <Link 
                    to="/giris" 
                    state={{ role: 'admin' }}
                    className="dropdown-item"
                    onClick={closeMenuAndDropdown}
                  >
                    <span className="dropdown-item-icon"><Wrench size={18} /></span>
                    <div className="dropdown-item-content">
                      <span className="dropdown-item-title">Yönetici Girişi</span>
                      <span className="dropdown-item-desc">Admin paneline erişim</span>
                    </div>
                  </Link>
                  <Link 
                    to="/giris" 
                    state={{ role: 'technician' }}
                    className="dropdown-item"
                    onClick={closeMenuAndDropdown}
                  >
                    <span className="dropdown-item-icon"><Wrench size={18} /></span>
                    <div className="dropdown-item-content">
                      <span className="dropdown-item-title">Teknisyen Girişi</span>
                      <span className="dropdown-item-desc">Teknisyen paneline erişim</span>
                    </div>
                  </Link>
                </div>
              </div>
              
              <Link 
                to="/talep" 
                className="btn-primary header-cta" 
                onClick={() => trackButtonClick('online_talep', 'header')}
              >
                Online Talep
              </Link>
            </div>

            <button 
              className="mobile-menu-btn" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Menüyü aç/kapat"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Contact Bar */}
      <div className="mobile-contact-bar">
        <a href="tel:+905518478521" className="mobile-contact-btn phone" onClick={handlePhoneClick}>
          <Phone size={18} />
          Ara
        </a>
        <a 
          href="https://wa.me/905518478521?text=Merhaba,%20servis%20talebi%20oluşturmak%20istiyorum." 
          target="_blank" 
          rel="noopener noreferrer"
          className="mobile-contact-btn whatsapp"
          onClick={handleWhatsAppClick}
        >
          <MessageCircle size={18} />
          WhatsApp
        </a>
        <Link to="/talep" className="mobile-contact-btn request" onClick={closeMenuAndDropdown}>
          <Wrench size={18} />
          Talep
        </Link>
      </div>
    </header>
  );
};

export default Header;
